import { Database, CheckCircle, User, Search, Mail, Send, LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

export type PipelineStage =
  | 'sourced'
  | 'qualified'
  | 'analyzed'
  | 'contact'
  | 'outreach'
  | 'reached'

export type StageGroup = 'enrichment' | 'outreach'

interface StageConfig {
  label: string
  color: string
  darkColor: string
  icon: LucideIcon
  order: number
  group: StageGroup
  tooltip: string
}

export const STAGE_CONFIG: Record<PipelineStage, StageConfig> = {
  sourced: {
    label: 'Sourced',
    color: 'bg-muted text-muted-foreground border-border',
    darkColor: '',
    icon: Database,
    order: 1,
    group: 'enrichment',
    tooltip: 'Company discovered from a data source',
  },
  qualified: {
    label: 'Qualified',
    color: 'bg-chart-1/10 text-chart-1 border-chart-1/20',
    darkColor: '',
    icon: CheckCircle,
    order: 2,
    group: 'enrichment',
    tooltip: 'Enriched with qualification score based on fit criteria',
  },
  analyzed: {
    label: 'Analyzed',
    color: 'bg-chart-2/10 text-chart-2 border-chart-2/20',
    darkColor: '',
    icon: Search,
    order: 3,
    group: 'enrichment',
    tooltip: 'Enriched with website analysis and company insights',
  },
  contact: {
    label: 'Contact Found',
    color: 'bg-primary/10 text-primary border-primary/20',
    darkColor: '',
    icon: User,
    order: 4,
    group: 'enrichment',
    tooltip: 'Enriched with contact information (email, phone)',
  },
  outreach: {
    label: 'Outreach Ready',
    color: 'bg-chart-5/10 text-chart-5 border-chart-5/20',
    darkColor: '',
    icon: Mail,
    order: 5,
    group: 'outreach',
    tooltip: 'Personalized outreach content generated',
  },
  reached: {
    label: 'Reached Out',
    color: 'bg-chart-1/10 text-chart-1 border-chart-1/20',
    darkColor: '',
    icon: Send,
    order: 6,
    group: 'outreach',
    tooltip: 'Outreach sent to the company',
  },
}

export const ENRICHMENT_STAGES: PipelineStage[] = ['sourced', 'qualified', 'analyzed', 'contact']
export const OUTREACH_STAGES: PipelineStage[] = ['outreach', 'reached']

export const PIPELINE_STAGES: PipelineStage[] = [
  'sourced',
  'qualified',
  'analyzed',
  'contact',
  'outreach',
  'reached',
]

interface PipelineStageBadgeProps {
  stage: PipelineStage
  showIcon?: boolean
  showTooltip?: boolean
  size?: 'sm' | 'md'
  className?: string
}

export function PipelineStageBadge({
  stage,
  showIcon = true,
  showTooltip = false,
  size = 'md',
  className,
}: PipelineStageBadgeProps) {
  const config = STAGE_CONFIG[stage]
  const Icon = config.icon

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border font-medium',
        config.color,
        config.darkColor,
        size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-xs',
        className
      )}
      title={showTooltip ? config.tooltip : undefined}
    >
      {showIcon && (
        <Icon className={cn(size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5')} />
      )}
      {config.label}
    </span>
  )
}

interface StageProgressDotsProps {
  stage: PipelineStage
  className?: string
}

export function StageProgressDots({ stage, className }: StageProgressDotsProps) {
  const currentOrder = STAGE_CONFIG[stage].order
  const totalStages = PIPELINE_STAGES.length

  return (
    <div className={cn('flex items-center gap-1', className)}>
      {PIPELINE_STAGES.map((s, index) => {
        const isFilled = index < currentOrder
        return (
          <span
            key={s}
            className={cn(
              'h-2 w-2 rounded-full transition-colors',
              isFilled
                ? 'bg-primary'
                : 'bg-muted-foreground/20'
            )}
            title={STAGE_CONFIG[s].label}
          />
        )
      })}
    </div>
  )
}

export function getStageFromCompany(company: {
  qualified?: unknown
  hasAnalysis?: boolean
  outreach?: { sent?: boolean } | null
}): PipelineStage {
  const qualified = company.qualified as { contact_enrichment_status?: string } | null

  if (!qualified) {
    return 'sourced'
  }

  const hasContact = qualified.contact_enrichment_status === 'found'
  const hasAnalysis = company.hasAnalysis ?? false
  const outreach = company.outreach

  if (outreach) {
    return outreach.sent ? 'reached' : 'outreach'
  }

  if (hasAnalysis) {
    return 'analyzed'
  }

  if (hasContact) {
    return 'contact'
  }

  return 'qualified'
}
